/**
 * Landing Page V2 - Marketplace Banner Section
 *
 * Banner promoting the Nexgo Marketplace with product cards
 */

'use client';

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  strokeColors,
} from '@/design-system/utils';
import Image from 'next/image';
import { ScrollReveal } from './ScrollReveal';

interface ProductCardProps {
  imageSrc: string;
  title: string;
  location: string;
  price: string;
  currency: string;
  unit: string;
  width: string;
  imageHeight: string;
  isOnSale?: boolean;
  disableOverlap?: boolean;
  zIndex: number;
}

function ProductCard({
  imageSrc,
  title,
  location,
  price,
  currency,
  unit,
  width,
  imageHeight,
  isOnSale = false,
  disableOverlap = false,
  zIndex,
}: ProductCardProps) {
  const isMobile = width === '245px'; // Mobile cards use 245px width
  return (
    <div
      className='flex flex-col flex-shrink-0'
      style={{
        backgroundColor: backgroundColors.primary,
        border: `${borders.width.thin} solid ${strokeColors.light}`,
        gap: '6px',
        padding: spacing[2],
        borderRadius: '12px',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.06)',
        width: width,
        marginRight: disableOverlap || isMobile ? '0' : '-80px',
        zIndex: zIndex,
      }}
    >
      {/* Product Image */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: imageHeight,
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: backgroundColors.shade, // Fallback background
        }}
      >
        <Image
          src={imageSrc}
          alt={title}
          fill
          style={{ objectFit: 'cover' }}
          onError={e => {
            // Fallback to placeholder if image fails to load
            const target = e.target as HTMLImageElement;
            if (target.src !== '/assets/images/No-Image.jpg') {
              target.src = '/assets/images/No-Image.jpg';
            }
          }}
        />
      </div>

      {/* Product Info */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[1],
          padding: `6px ${spacing[1]}`,
        }}
      >
        <p
          className='text-base md:text-lg font-semibold line-clamp-2'
          style={{
            fontFamily: fontPrimary,
            color: typographyColors.primary,
            lineHeight: 'normal',
            margin: 0,
          }}
        >
          {title}
        </p>
        <div
          style={{
            display: 'flex',
            gap: spacing[1],
            alignItems: 'center',
          }}
        >
          <svg
            width='12'
            height='12'
            viewBox='0 0 12 12'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M10.2001 5.00005C10.2001 7.76145 6.0001 11.4001 6.0001 11.4001C6.0001 11.4001 1.8001 7.76145 1.8001 5.00005C1.8001 3.89135 2.2425 2.82832 3.02515 2.04567C3.80779 1.26302 4.87083 0.820618 5.9795 0.820618C7.08818 0.820618 8.15121 1.26302 8.93386 2.04567C9.71651 2.82832 10.1589 3.89135 10.1589 5.00005H10.2001Z'
              fill='#27272a'
            />
            <ellipse cx='6.0001' cy='5.00005' rx='1.2' ry='1.2' fill='white' />
          </svg>
          <p
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.medium,
              fontSize: typography.size.xs,
              color: '#57534e',
              lineHeight: 'normal',
              margin: 0,
            }}
          >
            {location}
          </p>
        </div>
      </div>

      {/* Price */}
      <div
        style={{
          backgroundColor: '#fefce8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isOnSale ? 'space-between' : 'center',
          gap: spacing[1],
          padding: isOnSale ? spacing[2] : spacing[3],
          borderRadius: '8px',
          lineHeight: 'normal',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '4px',
            alignItems: 'flex-end',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '0px',
              alignItems: 'flex-end',
              fontWeight: typography.weight.bold,
              color: typographyColors.primary,
            }}
          >
            <p
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.xs,
                margin: 0,
              }}
            >
              {currency}
            </p>
            <p
              className='text-base md:text-xl'
              style={{
                fontFamily: fontPrimary,
                margin: 0,
              }}
            >
              {price.split('.')[0]}
            </p>
            <p
              style={{
                fontFamily: fontPrimary,
                fontSize: '16px',
                margin: 0,
              }}
            >
              .
            </p>
            <p
              style={{
                fontFamily: fontPrimary,
                fontSize: '12px',
                margin: 0,
              }}
            >
              {price.split('.')[1] || '00'}
            </p>
          </div>
          <p
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.regular,
              fontSize: typography.size.xs,
              color: '#57534e',
              margin: 0,
            }}
          >
            /{unit}
          </p>
        </div>
        {isOnSale && (
          <div
            style={{
              backgroundColor: '#fde047',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              borderRadius: '8px',
            }}
          >
            <p
              style={{
                fontFamily: fontPrimary,
                fontWeight: typography.weight.bold,
                fontSize: typography.size.sm,
                color: typographyColors.primary,
                lineHeight: 'normal',
                margin: 0,
              }}
            >
              On 50% Sale
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function MarketplaceBannerSection() {
  return (
    <ScrollReveal
      as='section'
      className='px-4 md:px-20 py-8 md:py-12'
      direction='up'
      amount={0.2}
    >
      <div className='relative flex flex-col md:flex-row md:items-center p-6 md:p-12 rounded-2xl overflow-hidden max-w-[1394px] mx-auto min-h-[auto] md:min-h-[429px]'>
        {/* Background with gradient and texture */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '16px',
            background:
              'linear-gradient(117.7deg, rgba(253, 224, 71, 1) 25.406%, rgba(255, 217, 120, 1) 85.643%)',
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '16px',
            backgroundImage:
              "url('/assets/images/marketplace/marketplace-texture.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'soft-light',
            opacity: 0.3,
            zIndex: 1,
          }}
        />

        {/* Content Container */}
        <div className='relative z-[2] flex flex-col md:flex-row md:items-center w-full gap-6 md:gap-[116px]'>
          {/* Left Content */}
          <ScrollReveal
            className='flex flex-col justify-between w-full md:w-[483px] md:min-h-[333px] flex-shrink-0'
            delay={0.1}
            direction='left'
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[4], // 16px on mobile, spacing[5] on desktop
              }}
            >
              {/* Badge */}
              <div className='flex justify-center md:justify-start'>
                <div
                  className='flex items-center gap-2.5 rounded-md px-3 py-1'
                  style={{ width: 'fit-content' }}
                >
                  <div
                    className='flex items-center justify-center rounded-full'
                    style={{
                      backgroundColor: '#fefce8',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      padding: '8px', // spacing[2] = 8px
                    }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- SVG icon */}
                    <img
                      src='/assets/images/marketplace/marketplace-icon.svg'
                      alt='Marketplace icon'
                      width={24}
                      height={24}
                      style={{ objectFit: 'contain', aspectRatio: '1/1' }}
                    />
                  </div>
                  <p
                    className='text-[20px]'
                    style={{
                      fontFamily: fontPrimary, // Gantari
                      fontWeight: 700,
                      color: typographyColors.primary, // #1C1917
                      lineHeight: 'normal',
                      margin: 0,
                    }}
                  >
                    Nexgo Marketplace
                  </p>
                </div>
              </div>

              {/* Mobile Images - Between badge and title */}
              <ScrollReveal
                className='flex md:hidden w-full overflow-x-auto'
                style={{
                  paddingLeft: 0,
                  paddingRight: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                  WebkitOverflowScrolling: 'touch',
                }}
                delay={0.2}
                direction='right'
              >
                <div
                  className='flex items-center'
                  style={{
                    gap: spacing[3],
                    paddingRight: spacing[2],
                  }}
                >
                  <ProductCard
                    imageSrc='/assets/images/marketplace/camel-milk.jpg'
                    title='Fresh Camel Milk'
                    location='Abu Dhabi, UAE'
                    price='99.99'
                    currency='AED'
                    unit='liter'
                    width='180px'
                    imageHeight='120px'
                    disableOverlap={true}
                    zIndex={3}
                  />
                  <ProductCard
                    imageSrc='/assets/images/marketplace/damascus-knife.jpg'
                    title='Locally Made Damascus Steel Knife'
                    location='Damascus, Beirut'
                    price='5,400.75'
                    currency='₹'
                    unit='item'
                    width='300px'
                    imageHeight='180px'
                    isOnSale={true}
                    disableOverlap={true}
                    zIndex={2}
                  />
                  <ProductCard
                    imageSrc='/assets/images/marketplace/bakoor-oud.jpg'
                    title='Premium Bakoor Oud Wood'
                    location='Dubai, UAE'
                    price='275.50'
                    currency='AED'
                    unit='piece'
                    width='200px'
                    imageHeight='140px'
                    disableOverlap={true}
                    zIndex={1}
                  />
                </div>
              </ScrollReveal>

              {/* Title and Description */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing[4], // 16px
                  color: 'black',
                }}
              >
                <p
                  className='text-center md:text-left text-[30px] md:text-[48px] font-semibold w-full'
                  style={{
                    fontFamily: fontPrimary, // Gantari
                    fontWeight: 600, // semibold
                    color: '#000',
                    lineHeight: 'normal',
                    alignSelf: 'stretch',
                    margin: 0,
                  }}
                >
                  Discover authentic local products from every destination.
                </p>

                <p
                  className='text-center md:text-left text-[16px] leading-[20px] w-full'
                  style={{
                    fontFamily: fontPrimary, // Gantari
                    fontWeight: 400, // regular
                    color: '#000',
                    lineHeight: '20px',
                    alignSelf: 'stretch',
                    margin: 0,
                  }}
                >
                  Shop handcrafted goods, regional specialties, and cultural
                  treasures — all curated with care and authenticity.
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <a
              href='https://marketplace.nexgo.ai'
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center justify-center gap-2.5 w-full md:w-fit'
              style={{
                marginTop: '36px',
                padding: spacing[4], // 16px
                borderRadius: '12px', // extra-large border radius
                background: '#FFF', // base color
                boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.12)',
                textDecoration: 'none',
                cursor: 'pointer',
                alignSelf: 'stretch',
                transition: 'transform 0.2s ease',
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.transform = 'scale(1.02)')
              }
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <p
                className='text-base md:text-2xl font-semibold'
                style={{
                  fontFamily: fontPrimary,
                  color: typographyColors.primary,
                  lineHeight: 'normal',
                  margin: 0,
                }}
              >
                Explore the marketplace
              </p>
              {/* eslint-disable-next-line @next/next/no-img-element -- SVG icon */}
              <img
                src='/assets/images/marketplace/arrow-up-right.svg'
                alt='Arrow'
                width={16}
                height={16}
                className='md:w-5 md:h-5'
                style={{ objectFit: 'contain' }}
              />
            </a>
          </ScrollReveal>

          {/* Right Content - Product Cards Desktop */}
          <ScrollReveal
            className='hidden md:flex items-center'
            style={{
              height: '429px',
              paddingLeft: 0,
              paddingRight: '80px',
              paddingTop: 0,
              paddingBottom: 0,
              justifyContent: 'flex-end',
              flex: 1,
            }}
            delay={0.2}
            direction='right'
          >
            <ProductCard
              imageSrc='/assets/images/marketplace/camel-milk.jpg'
              title='Fresh Camel Milk'
              location='Abu Dhabi, UAE'
              price='99.99'
              currency='AED'
              unit='liter'
              width='180px'
              imageHeight='120px'
              zIndex={3}
            />
            <ProductCard
              imageSrc='/assets/images/marketplace/damascus-knife.jpg'
              title='Locally Made Damascus Steel Knife'
              location='Damascus, Beirut'
              price='5,400.75'
              currency='₹'
              unit='item'
              width='300px'
              imageHeight='180px'
              isOnSale={true}
              zIndex={2}
            />
            <ProductCard
              imageSrc='/assets/images/marketplace/bakoor-oud.jpg'
              title='Premium Bakoor Oud Wood'
              location='Dubai, UAE'
              price='275.50'
              currency='AED'
              unit='piece'
              width='200px'
              imageHeight='140px'
              zIndex={1}
            />
          </ScrollReveal>

          {/* Mobile: Overlapping product images above text (per Figma design) */}
          {/* 
            TODO: Add correct marketplace product images matching Figma design:
            - Left: White ceramic mug/vessel (smaller, 80px)
            - Middle: Aerial city view at sunrise/sunset (largest, 120px) 
            - Right: Decorative handmade object with patterns (smaller, 70px)
            
            Images should be:
            - Overlapping with negative margins (-15px)
            - Slightly rotated for natural look
            - Positioned at top center (20% from top)
            - Added to /public/assets/images/marketplace/ folder
            
            Once images are added, uncomment and update the image paths below:
          */}
          {/* 
          <div className='md:hidden absolute inset-0 overflow-visible rounded-2xl pointer-events-none z-[1]'>
            <div
              className='absolute flex items-center justify-center'
              style={{
                top: '20%',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              <div
                className='relative rounded-lg overflow-hidden shadow-lg'
                style={{
                  width: '80px',
                  height: '80px',
                  backgroundImage: "url('/assets/images/marketplace/white-mug.jpg')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transform: 'rotate(-3deg)',
                  zIndex: 1,
                  marginRight: '-15px',
                }}
              />
              <div
                className='relative rounded-lg overflow-hidden shadow-lg'
                style={{
                  width: '120px',
                  height: '120px',
                  backgroundImage: "url('/assets/images/marketplace/cityscape.jpg')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transform: 'rotate(2deg)',
                  zIndex: 2,
                }}
              />
              <div
                className='relative rounded-lg overflow-hidden shadow-lg'
                style={{
                  width: '70px',
                  height: '70px',
                  backgroundImage: "url('/assets/images/marketplace/decorative-item.jpg')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transform: 'rotate(-2deg)',
                  zIndex: 1,
                  marginLeft: '-15px',
                }}
              />
            </div>
          </div>
          */}
        </div>
      </div>
    </ScrollReveal>
  );
}
